/**
 * Meta (Facebook) Pixel helper.
 *
 * The base pixel is loaded from /public/index.html so it's available on every page.
 * This wrapper exposes safe helpers to fire standard + custom events from React code
 * without crashing if the pixel is blocked (ad-blockers) or hasn't loaded yet.
 *
 * Standard events we currently fire:
 *   - PageView             (auto — base pixel + SPA route change tracker below)
 *   - Lead                 (support widget submit, landing hero waitlist)
 *   - CompleteRegistration (successful OTP verify → new account)
 *   - Search               (dashboard search / debtors filter, if used)
 *   - AddToCart            (adding an item to a quotation/invoice)
 *   - InitiateCheckout     (opening the Paystack checkout redirect)
 *   - Purchase             (successful Paystack payment webhook confirmation on client)
 *
 * All helpers no-op silently if window.fbq isn't present.
 */

const isBrowser = typeof window !== "undefined";

function safeFbq(...args) {
  try {
    if (isBrowser && typeof window.fbq === "function") {
      window.fbq(...args);
    }
  } catch {
    /* swallow — never break the app for analytics */
  }
}

/** Track a standard Meta event. */
export function trackStandard(eventName, params) {
  if (params && Object.keys(params).length) {
    safeFbq("track", eventName, params);
  } else {
    safeFbq("track", eventName);
  }
}

/** Track a custom event (uses trackCustom under the hood). */
export function trackCustom(eventName, params) {
  if (params && Object.keys(params).length) {
    safeFbq("trackCustom", eventName, params);
  } else {
    safeFbq("trackCustom", eventName);
  }
}

// -------- Convenience helpers ---------------------------------------------
export const pixel = {
  pageView: () => trackStandard("PageView"),
  lead: (params) => trackStandard("Lead", params),
  completeRegistration: (params) => trackStandard("CompleteRegistration", params),
  search: (params) => trackStandard("Search", params),
  addToCart: (params) => trackStandard("AddToCart", params),
  initiateCheckout: (params) => trackStandard("InitiateCheckout", params),
  purchase: (params) => trackStandard("Purchase", params),
  contact: (params) => trackStandard("Contact", params),
  submitApplication: (params) => trackStandard("SubmitApplication", params),
  custom: trackCustom,
};

export default pixel;
